import React from 'react';
import { Link } from 'react-router-dom';

export default function TermsOfUse() {
  return (
    <div className="bg-gray-50 min-h-screen">
      <section className="relative py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Terms of Use</h1>
            <p className="text-lg text-gray-300">
              Terms governing the use of MGL Experiences Ltd's website and business engagement.
            </p>
          </div>
        </div>
      </section>

      <div className="section-container">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Terms Overview</h3>
                <nav className="space-y-2">
                  <a href="#acceptance" className="block text-gray-600 hover:text-primary">Acceptance of Terms</a>
                  <a href="#website" className="block text-gray-600 hover:text-primary">Website Use</a>
                  <a href="#intellectual" className="block text-gray-600 hover:text-primary">Intellectual Property</a>
                  <a href="#representations" className="block text-gray-600 hover:text-primary">Representations</a>
                  <a href="#limitation" className="block text-gray-600 hover:text-primary">Limitation of Liability</a>
                  <a href="#governing" className="block text-gray-600 hover:text-primary">Governing Law</a>
                  <a href="#changes" className="block text-gray-600 hover:text-primary">Changes to Terms</a>
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="prose prose-lg max-w-none">
                  
                  <section id="acceptance" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                    <p className="text-gray-700 mb-4">
                      By accessing and using the MGL Experiences Ltd website (the "Site"), you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our Site.
                    </p>
                    <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                      <p className="text-gray-700">
                        <strong>Important:</strong> This Site is intended for B2B (business-to-business) engagement only. It is not designed for consumer travel inquiries or bookings.
                      </p>
                    </div>
                  </section>

                  <section id="website" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Website Use</h2>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Permitted Use</h3>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Business partnership inquiries and research</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Understanding our experience IP portfolio</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Contacting us for partnership opportunities</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Accessing legal and compliance information</span>
                      </li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Prohibited Activities</h3>
                    <div className="bg-red-50 p-4 rounded-lg mb-6">
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Attempting to book travel or make reservations</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Scraping, copying, or reproducing site content without permission</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Misrepresenting your business or intentions</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Engaging in any unlawful or fraudulent activity</span>
                        </li>
                      </ul>
                    </div>
                  </section>

                  <section id="intellectual" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Intellectual Property</h2>
                    
                    <div className="bg-blue-50 p-6 rounded-lg mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Ownership Notice</h3>
                      <p className="text-gray-700 mb-4">
                        All content on this Site, including but not limited to text, graphics, logos, images, and the overall design, is the property of MGL Experiences Ltd and is protected by UK and international intellectual property laws.
                      </p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 bg-white rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-2">Protected Elements</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>Experience IP formats and designs</li>
                            <li>Brand names and logos</li>
                            <li>Website content and copy</li>
                            <li>Proprietary methodologies</li>
                          </ul>
                        </div>
                        <div className="p-4 bg-white rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-2">Restricted Use</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>No commercial reproduction</li>
                            <li>No derivative works</li>
                            <li>No redistribution</li>
                            <li>License required for use</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section id="representations" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Representations and Warranties</h2>
                    
                    <div className="space-y-6">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Business Representation</h4>
                        <p className="text-gray-700">
                          By contacting us through this Site, you represent and warrant that you are acting on behalf of a legitimate business entity and have the authority to enter into discussions regarding potential partnerships.
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Accuracy of Information</h4>
                        <p className="text-gray-700">
                          You warrant that all information provided to us is accurate, complete, and not misleading. We reserve the right to verify the information provided and to terminate discussions if false information is detected.
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Compliance with Laws</h4>
                        <p className="text-gray-700">
                          You agree to comply with all applicable laws and regulations in your use of this Site and in any subsequent business dealings with MGL Experiences Ltd.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section id="limitation" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Limitation of Liability</h2>
                    
                    <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Important Limitations</h3>
                      
                      <div className="space-y-4">
                        <p className="text-gray-700">
                          <strong>No Warranty:</strong> This Site is provided "as is" without warranties of any kind, either express or implied. We do not warrant that the Site will be uninterrupted or error-free.
                        </p>
                        
                        <p className="text-gray-700">
                          <strong>Exclusion of Liability:</strong> To the fullest extent permitted by law, MGL Experiences Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Site.
                        </p>
                        
                        <p className="text-gray-700">
                          <strong>Maximum Liability:</strong> Our maximum aggregate liability under or in connection with these Terms shall not exceed £1,000.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section id="governing" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Governing Law and Jurisdiction</h2>
                    
                    <div className="p-6 bg-gray-100 rounded-lg">
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-6 h-6 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-700 font-medium">
                            These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these Terms or your use of the Site shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section id="changes" className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Changes to Terms</h2>
                    
                    <div className="p-6 bg-blue-50 rounded-lg">
                      <p className="text-gray-700 mb-4">
                        We reserve the right to modify these Terms of Use at any time. Changes will be effective immediately upon posting to the Site. Your continued use of the Site following the posting of changes constitutes your acceptance of those changes.
                      </p>
                      
                      <div className="flex items-center mt-4">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-gray-600">
                          We recommend reviewing these Terms periodically for updates.
                        </p>
                      </div>
                    </div>
                  </section>

                </div>
              </div>

              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <Link
                      to="/legal-compliance"
                      className="text-sm text-primary font-medium hover:text-primary-dark"
                    >
                      ← Back to Legal & Compliance
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}